import { Link } from "react-router-dom";
import { COMPANY } from "../lib/company";
import { PARTNERS } from "./partners";
// The resource hub's stylesheet, deliberately: the partner index is the same
// kind of page — a short argument followed by a list of places to go — and it
// should read as one system with /resources rather than as a second design.
import "../resources/ResourcesHub.css";

/**
 * `/partners` — the partner index.
 *
 * Named `PartnersPage`, not `Partners`, because `partners.ts` already sits beside
 * it and two files whose names differ only in case cannot coexist on a
 * case-insensitive filesystem — TypeScript refuses the program outright.
 *
 * WHY THIS PAGE EXISTS. `/partners/graphaize` and `/partners/content-creator`
 * were both reachable only from a link somebody already had. There was no place
 * that said what a partner IS to this product, so a reader arriving from the
 * nav had nowhere to land and the two one-pagers looked like orphans.
 *
 * It borrows the resource hub's card design rather than inventing one, and the
 * `?partner=` convention the contact page already understands, so a partner
 * enquiry started here arrives with its context intact.
 */

/** Why partnership matters. The argument, before the list. */
const REASONS = [
  {
    number: "01",
    title: "Software cannot art-direct.",
    body:
      "Simino Social schedules, publishes and reports. It does not decide what is worth " +
      "publishing, hold a brand system in its head, or judge when a piece is not good " +
      "enough yet. Those are people, and pretending otherwise is how brands end up posting " +
      "consistently and saying nothing.",
  },
  {
    number: "02",
    title: "One supplier, one conversation.",
    body:
      "Commissioning creative and running the channels are usually two vendors, two " +
      "invoices and a gap between them where the brief gets lost. Coming through us means " +
      "the work is quoted, produced and published in one thread.",
  },
  {
    number: "03",
    title: "A bundle, in writing, before anything is committed.",
    body:
      "Partner work and your Simino Social plan are quoted together. You see the whole " +
      "number before you agree to any of it — no introduction fee, no surprise line item " +
      "at the end.",
  },
  {
    number: "04",
    title: "We only introduce people we would use.",
    body:
      "The list below is short on purpose and will stay that way. A directory of everyone " +
      "who asked to be on it is worth nothing to the person reading it.",
  },
];

function PartnerRow({ to, icon, label, title, description, action }: {
  to: string; icon: string; label: string; title: string; description: string; action: string;
}) {
  return (
    <Link className="hub-resource-row" to={to}>
      <span className="hub-row-icon" aria-hidden="true">{icon}</span>
      <div className="hub-row-copy">
        <span className="eyebrow">{label}</span>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <span className="hub-row-action">{action}<span aria-hidden="true">→</span></span>
    </Link>
  );
}

export function PartnersPage() {
  const graphaize = PARTNERS.graphaize;

  return (
    <div className="scheduling-story resource-index resource-hub">
      <header className="hero hub-hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <Link to="/" className="scheduling-back">← {COMPANY.product}</Link>
            <p className="hero-badge"><span aria-hidden="true">✦</span> Partners</p>
            <h1>
              The tool is ours.
              <span className="hero-gradient-text">The craft is<br />worth sharing.</span>
            </h1>
            <p className="lede">
              {COMPANY.product} gets your content out on time, to the right places, with a
              record of what happened. What it deliberately does not do is make the work
              itself. For that we introduce you to people who do it properly.
            </p>
            <div className="hero-cta">
              <Link to="/contact?topic=partnership" className="btn-link primary lg">
                Partner With Us <span aria-hidden="true">→</span>
              </Link>
              <a href="#our-partners" className="btn-link lg">
                Meet our partners <span aria-hidden="true">↓</span>
              </a>
            </div>
            <div className="hub-stats" aria-label="Partner programme">
              <span><strong>01</strong> creative partner</span>
              <span><strong>00</strong> introduction fees</span>
              <span><strong>1</strong> quote, both halves</span>
            </div>
          </div>

          <div className="hub-reading-path">
            <div className="hub-path-heading">
              <span className="eyebrow">What brings you here?</span>
              <span aria-hidden="true">✦</span>
            </div>
            <h2>Two ways in.<br />Same mailbox.</h2>
            <p>Pick whichever describes you.</p>
            <a href="#why-partnership">
              <span className="hub-path-number">01</span>
              <span><strong>“I need something made.”</strong><small>See who we work with</small></span>
              <span aria-hidden="true">↗</span>
            </a>
            <a href="#our-partners">
              <span className="hub-path-number">02</span>
              <span><strong>“Why not just use AI?”</strong><small>The honest answer</small></span>
              <span aria-hidden="true">↗</span>
            </a>
            <a href="#become-a-partner">
              <span className="hub-path-number">03</span>
              <span><strong>“We do this work.”</strong><small>Become a partner</small></span>
              <span aria-hidden="true">↗</span>
            </a>
            <div className="hub-path-footnote">
              <span aria-hidden="true">◇</span> A person reads every enquiry.
            </div>
          </div>
        </div>
      </header>

      <nav className="hub-topic-nav" aria-label="Partner topics">
        <div className="band-inner">
          <span>On this page</span>
          <a href="#why-partnership">Why partnership matters</a>
          <a href="#our-partners">Our partners</a>
          <a href="#how-it-works">How it works</a>
          <a href="#become-a-partner">Become a partner</a>
        </div>
      </nav>

      <section className="band hub-start" id="why-partnership" aria-labelledby="why-partnership-title">
        <div className="band-inner">
          <div className="hub-section-head">
            <div>
              <p className="hub-section-kicker"><span>01</span> WHY PARTNERSHIP MATTERS</p>
              <h2 id="why-partnership-title" className="section-title">
                Publishing is a habit.<br />
                <span className="hero-gradient-text">Being worth reading is a skill.</span>
              </h2>
            </div>
            <p>
              Most social tools sell you the habit and leave the skill as your problem. We
              would rather be honest about the line and tell you who is on the other side
              of it.
            </p>
          </div>

          <div className="hub-control-steps" aria-label="Why partnership matters">
            {REASONS.map(reason => (
              <div key={reason.number}>
                <span>{reason.number}</span>
                <h3>{reason.title}</h3>
                <p>{reason.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band hub-comparison-group" id="our-partners" aria-labelledby="our-partners-title">
        <div className="band-inner">
          <div className="hub-section-head">
            <div>
              <p className="hub-section-kicker"><span>02</span> OUR PARTNERS</p>
              <h2 id="our-partners-title" className="section-title">Who we introduce you to.</h2>
            </div>
            <p>
              Read the profile before you enquire. Knowing what somebody actually makes is
              what turns a first message into a useful one.
            </p>
          </div>

          <div className="hub-resource-list">
            <PartnerRow
              to={graphaize.profile}
              icon="◈"
              label="Creative partner · Explainer video, design, decks"
              title={graphaize.name}
              description={graphaize.blurb}
              action="Read the profile"
            />
            <PartnerRow
              to="/partners/content-creator"
              icon="✎"
              label="The argument"
              title="AI can make the image. A human makes it mean something."
              description={
                "Why a specialist content creator is what makes a brand look like itself — " +
                "and why the part AI cannot do is the part that decides whether anyone cares."
              }
              action="Read the case"
            />
          </div>
        </div>
      </section>

      <section className="band alt hub-ai" id="how-it-works" aria-labelledby="how-it-works-title">
        <div className="band-inner">
          <div className="hub-section-head">
            <div>
              <p className="hub-section-kicker"><span>03</span> HOW IT WORKS</p>
              <h2 id="how-it-works-title" className="section-title">
                Three steps.<br /><span className="hero-gradient-text">No obligation at any of them.</span>
              </h2>
            </div>
            <p>
              Nothing is committed until you have seen the whole number in writing.
            </p>
          </div>
          <div className="hub-control-steps" aria-label="How a partner enquiry works">
            {[
              ["01", "Tell us what you need made", "The thing you are trying to explain, who needs to understand it, and roughly when. A real date changes what is worth proposing more than any other detail."],
              ["02", "We make the introduction", "Straight to the person who will actually do the work, with your brief already in their hands — not to a sales queue."],
              ["03", "One quote, both halves", "Production and your Simino Social plan, priced together and confirmed in writing before anything starts."],
            ].map(([n, title, body]) => (
              <div key={n}>
                <span>{n}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band hub-research" id="become-a-partner" aria-labelledby="become-a-partner-title">
        <div className="band-inner hub-research-inner">
          <div>
            <p className="eyebrow">For studios and freelancers</p>
            <h2 id="become-a-partner-title">Do you make this work?</h2>
          </div>
          <div>
            <p>
              We are interested in a small number of people who are genuinely good at one
              thing — video, design, copy, photography — and who are happy to be judged on
              the work rather than the deck about the work.
            </p>
            <p>
              Send us examples of what you have made and who it was for. Choose
              “Partnership or agency” on the contact form and it comes to the right place.
            </p>
          </div>
        </div>
      </section>

      <section className="band scheduling-outro hub-outro">
        <div className="band-inner narrow">
          <p className="hero-badge">Ready when you are</p>
          <h2 className="section-title">
            Tell us what you need made.
            <span className="hero-gradient-text">We will read it.</span>
          </h2>
          <p className="lede">
            No account needed, and no form that disappears into a queue — you confirm your
            address from the link we send, and a person replies.
          </p>
          <div className="hero-cta">
            <Link to="/contact?topic=partnership" className="btn-link primary lg">
              Partner With Us <span aria-hidden="true">→</span>
            </Link>
            <Link to="/resources" className="btn-link lg">Explore the library</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
